/**
 * micro:bit 놀이터 3D 무대의 주인공 몸짓(2026-10-08 — 사용자 요청 "마이크로비트가 조작될 때마다 무대에서 관련된 액션") — 순수 규칙.
 * 보드(왼쪽 시뮬레이터 · 보드 거울)는 그대로 두고, 무대 가운데 주인공 캐릭터가 보드 조작을 따라 한다:
 *  - A 단추 → 깡충 · B 단추 → 한 바퀴 · A+B 함께 → 크게 점프
 *  - 흔들기 → 몸을 흔들흔들(손님도 신남) · 로고 터치 → 손 흔들기(살짝 깡충 + 고개)
 *  - 기울기 → 그쪽으로 몸을 기울이며 한 발짝
 *  - 화면 그림(아이콘)이 바뀜 → 머리 위 말풍선(그림에 맞는 이모지) · 새 음 → 음표
 * 시각은 ms(부르는 쪽 시계) — 같은 입력이면 같은 몸짓이다.
 */
import type { MbFrame } from "./live";
import { iconOf, toRemote } from "./live";
import { reactionOf } from "./play";

export type MbActionKind = "hop" | "spin" | "bigHop" | "wiggle" | "wave" | "icon" | "note";
export interface MbAction {
  readonly kind: MbActionKind;
  readonly at: number;
  /** icon · note — 말풍선 글자 */
  readonly text?: string;
}

/** 몸짓 길이(ms) */
export const ACTION_MS: Readonly<Record<MbActionKind, number>> = {
  hop: 500,
  spin: 700,
  bigHop: 800,
  wiggle: 700,
  wave: 600,
  icon: 1600,
  note: 900,
};

const rose = (prev: boolean | undefined, next: boolean | undefined) => !prev && !!next;

/** 직전 · 지금 보드 모습 → 새로 시작할 몸짓(눌린 순간 · 바뀐 순간만) */
export function actionsOf(prev: MbFrame | null, next: MbFrame | null, now: number): MbAction[] {
  if (!next) return [];
  const out: MbAction[] = [];
  const a = rose(prev?.a, next.a);
  const b = rose(prev?.b, next.b);
  // 둘 중 하나가 새로 눌렸고 지금 둘 다 눌려 있으면 A+B 함께
  if ((a || b) && next.a && next.b) out.push({ kind: "bigHop", at: now });
  else {
    if (a) out.push({ kind: "hop", at: now });
    if (b) out.push({ kind: "spin", at: now });
  }
  if (rose(prev?.shake, next.shake)) out.push({ kind: "wiggle", at: now });
  if (rose(prev?.logo, next.logo)) out.push({ kind: "wave", at: now });
  const icon = iconOf(next.leds);
  if (icon && icon !== (prev ? iconOf(prev.leds) : null))
    out.push({ kind: "icon", at: now, text: reactionOf(icon) });
  if (next.tone && prev?.tone && next.tone.n !== prev.tone.n && next.tone.hz > 20)
    out.push({ kind: "note", at: now, text: "🎵" });
  return out;
}

/** 아직 도는 몸짓만(오래된 것은 버린다) */
export const liveActions = (xs: readonly MbAction[], now: number): MbAction[] =>
  xs.filter((x) => now - x.at < ACTION_MS[x.kind]);

export interface MbPose {
  /** 위로(m) · 옆 · 앞(m) */
  readonly y: number;
  readonly x: number;
  readonly z: number;
  /** 돌기(라디안) — y 축 · 좌우 기울기(z) · 앞뒤 기울기(x) */
  readonly rotY: number;
  readonly rotZ: number;
  readonly rotX: number;
  /** 몸짓이 도는 중(다음 그림이 필요) */
  readonly moving: boolean;
}

const LEAN: Record<string, readonly [number, number]> = {
  n: [0, -1],
  s: [0, 1],
  e: [1, 0],
  w: [-1, 0],
  ne: [0.7, -0.7],
  nw: [-0.7, -0.7],
  se: [0.7, 0.7],
  sw: [-0.7, 0.7],
};

/** 지금 몸짓들 · 기울기 → 주인공 자세(키 size m 기준) */
export function poseAt(
  actions: readonly MbAction[],
  frame: MbFrame | null,
  now: number,
  size = 0.2,
): MbPose {
  let y = 0;
  let rotY = 0;
  let rotZ = 0;
  let moving = false;
  for (const x of actions) {
    const p = Math.min(1, Math.max(0, (now - x.at) / ACTION_MS[x.kind]));
    if (p >= 1) continue;
    moving = true;
    const arc = Math.sin(p * Math.PI);
    if (x.kind === "hop") y = Math.max(y, arc * size * 0.45);
    else if (x.kind === "bigHop") {
      y = Math.max(y, arc * size * 0.9);
      rotY += p * Math.PI * 2;
    } else if (x.kind === "spin") rotY += p * Math.PI * 2;
    else if (x.kind === "wiggle") rotZ += Math.sin(p * Math.PI * 8) * 0.35 * (1 - p);
    else if (x.kind === "wave") {
      y = Math.max(y, arc * size * 0.15);
      rotZ += Math.sin(p * Math.PI * 4) * 0.15;
    }
  }
  const dir = toRemote(frame).dpad;
  const [lx, lz] = (dir && LEAN[dir]) || [0, 0];
  return {
    y,
    x: lx * size * 0.35,
    z: lz * size * 0.35,
    rotY,
    rotZ: rotZ - lx * 0.3,
    rotX: lz * 0.3,
    moving,
  };
}

/** 말풍선(가장 최근의 아이콘 · 음표 — 없으면 null) */
export function bubbleOf(actions: readonly MbAction[], now: number): string | null {
  const live = liveActions(actions, now).filter((x) => x.text);
  return live.length ? live[live.length - 1]!.text! : null;
}
